import { motion, AnimatePresence } from 'framer-motion'
import { X, Users } from 'lucide-react'
import { useNostrProfile } from '../../hooks/useNostrProfile'
import { UserAvatar } from '../Common/UserAvatar'
import { Link } from 'react-router-dom'
import { nip19 } from 'nostr-tools'

interface FollowersModalProps {
    isOpen: boolean
    onClose: () => void
    pubkeys: string[]
    title: string
}

export function FollowersModal({ isOpen, onClose, pubkeys, title }: FollowersModalProps) {
    if (!isOpen) return null

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="bg-white dark:bg-surface-900 rounded-3xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden border border-surface-200 dark:border-surface-700"
                >
                    {/* Header */}
                    <div className="p-6 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-mavi-500/10 rounded-xl flex items-center justify-center">
                                <Users className="w-5 h-5 text-mavi-600 dark:text-mavi-400" />
                            </div>
                            <div>
                                <h2 className="font-poppins font-bold text-lg text-gray-900 dark:text-white">{title}</h2>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{pubkeys.length} kişi</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* User List */}
                    <div className="p-4 space-y-2 overflow-y-auto max-h-[calc(80vh-120px)] custom-scrollbar">
                        {pubkeys.length === 0 ? (
                            <div className="text-center py-12">
                                <p className="text-gray-500 dark:text-gray-400 font-poppins text-sm">
                                    Henüz kimse yok
                                </p>
                            </div>
                        ) : (
                            pubkeys.map((pubkey) => (
                                <UserItem key={pubkey} pubkey={pubkey} onClose={onClose} />
                            ))
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    )
}

function UserItem({ pubkey, onClose }: { pubkey: string; onClose: () => void }) {
    const { metadata } = useNostrProfile(pubkey)
    const npub = nip19.npubEncode(pubkey)

    return (
        <Link
            to={`/profile/${pubkey}`}
            onClick={onClose}
            className="flex items-center gap-3 p-3 hover:bg-surface-50 dark:hover:bg-surface-800 rounded-xl transition-colors"
        >
            <UserAvatar
                src={metadata?.picture}
                name={metadata?.name || npub}
                size="lg"
                isExternal={false}
            />
            <div className="flex-1 min-w-0">
                <p className="font-poppins font-bold text-sm text-gray-900 dark:text-white truncate">
                    {metadata?.display_name || metadata?.name || npub.slice(0, 12) + '...'}
                </p>
                {metadata?.about && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {metadata.about}
                    </p>
                )}
            </div>
        </Link>
    )
}
